import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { canUseAdminShell, canAccessAdminRoute, defaultAdminPath } from "@/lib/permissions";
import {
  LayoutDashboard, Building2, Users, ClipboardList, CalendarCheck,
  TrendingUp, Wallet, FileSpreadsheet, ScanLine, Package, Sprout, LogOut, ChevronRight,
  ShoppingCart, Warehouse, ChefHat, History, CalendarDays, ScrollText,
  FileSignature, UserPlus, Network, FolderLock, FileCheck2, KeyRound, ShieldCheck,
  Compass
} from "lucide-react";

const groups = [
  {
    title: "Tổng quan",
    items: [
      { to: "/admin", permission: "dashboard", icon: LayoutDashboard, label: "Dashboard Holding", end: true, testid: "nav-dashboard" },
      { to: "/admin/menu-guideline", permission: "menu-guideline", icon: Compass, label: "Hướng dẫn Menu", testid: "nav-menu-guideline" },
    ]
  },
  {
    title: "Tổ chức",
    items: [
      { to: "/admin/companies", permission: "companies", icon: Building2, label: "Pháp nhân & Phòng ban", testid: "nav-companies" },
      { to: "/admin/employees", permission: "employees", icon: Users, label: "Nhân sự", testid: "nav-employees" },
      { to: "/admin/contracts", permission: "contracts", icon: FileSignature, label: "Hợp đồng", testid: "nav-contracts" },
      { to: "/admin/onboarding", permission: "onboarding", icon: UserPlus, label: "Onboarding", testid: "nav-onboarding" },
      { to: "/admin/org-chart", permission: "org-chart", icon: Network, label: "Sơ đồ tổ chức", testid: "nav-org-chart" },
      { to: "/admin/master", permission: "master", icon: Package, label: "Master Data", testid: "nav-master" },
    ]
  },
  {
    title: "HR-Ops",
    items: [
      { to: "/admin/tasks", permission: "tasks", icon: ClipboardList, label: "Task & SOP", testid: "nav-tasks" },
      { to: "/admin/attendance", permission: "attendance", icon: CalendarCheck, label: "Chấm công", testid: "nav-attendance" },
      { to: "/admin/shifts", permission: "shifts", icon: CalendarDays, label: "Ca làm", testid: "nav-shifts" },
      { to: "/admin/kpi", permission: "kpi", icon: TrendingUp, label: "KPI", testid: "nav-kpi" },
      { to: "/admin/payroll", permission: "payroll", icon: Wallet, label: "Lương & Thưởng A/B/C", testid: "nav-payroll" },
    ]
  },
  {
    title: "Vận hành",
    items: [
      { to: "/admin/pos", permission: "pos", icon: ShoppingCart, label: "POS đa kênh", testid: "nav-pos" },
      { to: "/admin/stock", permission: "stock", icon: Warehouse, label: "Kho tổng", testid: "nav-stock" },
      { to: "/admin/kitchen", permission: "kitchen", icon: ChefHat, label: "Bếp tổng BOM/MRP", testid: "nav-kitchen" },
      { to: "/admin/htx", permission: "htx", icon: Sprout, label: "HTX Nông sản", testid: "nav-htx" },
    ]
  },
  {
    title: "Công cụ",
    items: [
      { to: "/admin/excel", permission: "excel", icon: FileSpreadsheet, label: "Excel Workspace", testid: "nav-excel" },
      { to: "/admin/legacy-payroll", permission: "legacy-payroll", icon: History, label: "Import lương cũ", testid: "nav-legacy" },
      { to: "/admin/audit-history", permission: "audit-history", icon: ScrollText, label: "Audit History", testid: "nav-audit-history" },
      { to: "/admin/ocr", permission: "ocr", icon: ScanLine, label: "OCR Chứng từ (AI)", testid: "nav-ocr" },
    ]
  },
  {
    title: "Pháp lý",
    items: [
      { to: "/admin/legal-vault",    permission: "legal-vault",    icon: FolderLock,  label: "Kho tài liệu",   testid: "nav-legal-vault" },
      { to: "/admin/legal-requests", permission: "legal-requests", icon: FileCheck2,  label: "Phê duyệt yêu cầu", testid: "nav-legal-requests" },
      { to: "/admin/legal-grants",   permission: "legal-grants",   icon: KeyRound,    label: "Quản lý cấp",    testid: "nav-legal-grants" },
      { to: "/admin/legal-audit",    permission: "legal-audit",    icon: ShieldCheck, label: "Nhật ký pháp lý", testid: "nav-legal-audit" },
    ]
  },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-400">Đang khởi tạo…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!canUseAdminShell(user.role)) return <Navigate to="/staff" replace />;

  const visibleGroups = groups
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => canAccessAdminRoute(user.role, it.permission)),
    }))
    .filter((g) => g.items.length > 0);

  if (defaultAdminPath(user.role) === "/staff") return <Navigate to="/staff" replace />;

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-stone-200 flex flex-col flex-shrink-0">
        {/* Brand */}
        <div className="px-5 py-5 border-b border-stone-200">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-sm flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0F2A22, #186E54)" }}>
              <Sprout className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <div className="font-serif text-base font-bold leading-tight text-stone-900">Thanh Sơn</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-bold">BO Admin</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3" data-testid="admin-sidebar">
          {visibleGroups.map((g) => (
            <div key={g.title} className="px-3 mb-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-400 font-bold px-2 mb-1.5">
                {g.title}
              </div>
              {g.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  data-testid={it.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-2 py-2 rounded-sm text-sm transition-colors mb-0.5 ${
                      isActive
                        ? "bg-emerald-900 text-white font-semibold"
                        : "text-stone-700 hover:bg-stone-100 font-medium"
                    }`
                  }
                >
                  <it.icon className="w-4 h-4" strokeWidth={1.75} />
                  <span className="flex-1">{it.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* User */}
        <div className="border-t border-stone-200 p-3">
          <div className="flex items-center gap-2.5 mb-2 px-2">
            <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-900 font-bold flex items-center justify-center text-xs">
              {user.full_name?.[0] || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-stone-900 truncate">{user.full_name}</div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">{user.role}</div>
            </div>
          </div>
          <button
            onClick={() => { logout(); nav("/login"); }}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-2 py-2 text-sm text-stone-700 hover:bg-stone-100 rounded-sm"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} />
            Đăng xuất
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}

export function PageHeader({ title, breadcrumb, actions, kicker }) {
  return (
    <div className="bg-white border-b border-stone-200 px-6 lg:px-8 py-5">
      {breadcrumb && (
        <div className="flex items-center gap-1 text-xs text-stone-500 mb-1.5">
          {breadcrumb.map((b, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="w-3 h-3" />}
              <span className={i === breadcrumb.length - 1 ? "text-stone-900 font-medium" : ""}>{b}</span>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-end justify-between gap-4">
        <div>
          {kicker && <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-800 font-bold mb-1">{kicker}</div>}
          <h1 className="font-serif text-2xl lg:text-3xl font-bold text-stone-900 tracking-tight">{title}</h1>
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}
